/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { AlertTriangle, Inbox, RotateCw } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import BlankSlate from '@/components/shared/BlankSlate'

/** Server detail if there is one, otherwise the transport message. */
export function panelErrorMessage(error: unknown): string {
  const err = error as
    | { response?: { status?: number; data?: { detail?: unknown } }; message?: string }
    | null
    | undefined
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  const status = err?.response?.status
  if (status) return `Request failed (HTTP ${status})`
  return err?.message ?? 'Network error'
}

export function PanelSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-1.5" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-4 rounded bg-white/[0.03]"
          style={{ width: `${92 - (i % 4) * 11}%` }}
        />
      ))}
    </div>
  )
}

/**
 * Inline failure with a retry.
 *
 * Deliberately loud: a panel that fails silently and renders nothing reads as
 * "zero open matters", which is the most dangerous number on a legal board.
 */
export function PanelError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-md border border-red-500/25 bg-red-500/[0.04] px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-red-300">
        <AlertTriangle size={12} />
        Could not load this panel
      </div>
      <p className="text-[11px] leading-relaxed text-stone-400">{panelErrorMessage(error)}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
        >
          <RotateCw size={10} /> Retry
        </button>
      )}
    </div>
  )
}

export interface PanelProps {
  title: string
  /** Right-hand side of the header — a count, a period label, a legend. */
  kicker?: ReactNode
  actions?: ReactNode
  footer?: ReactNode
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  isEmpty?: boolean
  emptyTitle?: string
  emptyDescription?: ReactNode
  emptyIcon?: LucideIcon
  /** Shrink the empty slate so a 1×1 cell is a small card, not a full-bleed void. */
  compactEmpty?: boolean
  skeletonRows?: number
  /** Scroll the body inside the panel rather than growing the page. */
  scroll?: boolean
  className?: string
  bodyClassName?: string
  children?: ReactNode
}

/**
 * Titled panel wrapper. Loading, error and empty are handled here so no board
 * can accidentally ship a panel that presents an unknown as a zero.
 */
export default function Panel({
  title,
  kicker,
  actions,
  footer,
  isLoading = false,
  error = null,
  onRetry,
  isEmpty = false,
  emptyTitle = 'Nothing to show',
  emptyDescription = 'No records in this partition match this panel yet.',
  emptyIcon = Inbox,
  compactEmpty = false,
  skeletonRows = 5,
  scroll = false,
  className,
  bodyClassName,
  children,
}: PanelProps) {
  const body = () => {
    if (error) return <PanelError error={error} onRetry={onRetry} />
    if (isLoading) return <PanelSkeleton rows={skeletonRows} />
    if (isEmpty) {
      return (
        <BlankSlate
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
          compact={compactEmpty}
          className={
            compactEmpty
              ? '!border-transparent !bg-none !px-1.5 !py-1.5 !shadow-none'
              : '!border-transparent !bg-none !px-4 !py-6 !shadow-none'
          }
        />
      )
    }
    return children
  }

  return (
    <section className={clsx('panel flex min-h-0 flex-col', className)}>
      {/* The title is never cut: it keeps its natural width (wrapping only
          if it alone is wider than the panel), and the kicker takes what is
          left — wrapping, or dropping to its own line, rather than pushing
          the title into an ellipsis. */}
      <div className="panel-header flex-shrink-0 flex-wrap gap-x-3 gap-y-1">
        <span className="panel-header-title max-w-full flex-shrink-0 break-words">{title}</span>
        {(kicker || actions) && (
          <div className="ml-auto flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
            {kicker && <span className="min-w-0 text-right text-[10px] leading-snug text-stone-500">{kicker}</span>}
            {actions}
          </div>
        )}
      </div>
      <div
        className={clsx(
          'panel-body min-h-0 flex-1',
          scroll && 'overflow-y-auto scrollbar-thin',
          bodyClassName,
        )}
      >
        {body()}
      </div>
      {footer && (
        <div className="flex-shrink-0 border-t border-amber-400/[0.06] px-3 py-1.5 text-[10px] text-stone-500">
          {footer}
        </div>
      )}
    </section>
  )
}
